/* assets/glhk-property-intelligence.js — GLHK-PROPERTY-INTELLIGENCE-01
 * Runtime owner for property intelligence and manual review pages ([data-property-analysis]).
 * Sanitizes URLs, binds GPROPERTY_DATA_GATE local drafts, and updates public readiness states.
 * Data Gate Authority: assets/gproperty-flow-context.js & assets/gproperty-public-gateway.js.
 */
(function (window, document) {
  'use strict';

  var ROOT = '[data-property-analysis]';
  var VERSION = 'glhk-property-intelligence-01';
  var ALLOWED_INTENTS = {
    'property-analysis': true,
    family: true,
    buyer: true,
    owner: true,
    'property-service': true,
    'manual-verification': true,
  };

  /* ──────────────────────────────────────────────────────────────────────────
   * 1. DOM & URL SANITIZATION HELPERS
   * ────────────────────────────────────────────────────────────────────────── */
  function all(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function setText(root, selector, value) {
    var node = root.querySelector(selector);
    if (node) node.textContent = value;
  }

  function safeIntent(value, fallback) {
    var text = String(value || '')
      .trim()
      .toLowerCase();
    return ALLOWED_INTENTS[text] ? text : fallback;
  }

  function safeUrl(gate, base, intent, recordId) {
    var url;

    try {
      url = new URL(base, window.location.origin);
    } catch (_) {
      return base;
    }

    [
      'address',
      'address_zh',
      'address_en',
      'estate',
      'district',
      'budget',
      'school_net',
      'child',
      'phone',
      'email',
      'lead_id',
      'score',
      'service_lane',
    ].forEach(function (key) {
      url.searchParams.delete(key);
    });

    url.searchParams.set('source', 'glhk');
    url.searchParams.set('intent', safeIntent(intent, 'property-analysis'));

    if (gate.opaque(recordId)) {
      url.searchParams.set('record_id', recordId);
    } else {
      url.searchParams.delete('record_id');
    }

    return url.pathname + url.search + url.hash;
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 2. COMPONENT INITIALIZATION & DATA GATE BINDING
   * ────────────────────────────────────────────────────────────────────────── */
  /** @param {HTMLElement} root */
  function init(root) {
    if (!root || root.getAttribute('data-runtime-version') === VERSION) return;
    root.setAttribute('data-runtime-version', VERSION);

    var gate = window.GPROPERTY_DATA_GATE;
    if (!gate) return;

    var query = new URLSearchParams(window.location.search || '');
    var requestedRecord = gate.opaque(query.get('record_id'));
    var stored = gate.readPropertyDraft(gate.canonicalDraftKey, gate.legacyDraftKey);
    var draft = stored.draft;
    var draftRecord = gate.opaque(draft && draft.record_id);

    if (requestedRecord && requestedRecord !== draftRecord) {
      draft = null;
      draftRecord = '';
    }

    if (draft && draft.source === 'gproperty') {
      var verified = gate.isVerifiedDraft(draft);
      setText(
        root,
        '[data-glhk-context-title]',
        gate.locationLabel(draft.estate_context) || draft.project_location || '已連接物業脈絡'
      );
      setText(
        root,
        '[data-glhk-context-copy]',
        verified
          ? '已連接 HK DPO ALS 核實地址。完整地址仍只保留於本機，不寫入 URL。'
          : '目前只連接未核實本機文字；自動分析保持封鎖，但可提交人工服務需求。'
      );
      setText(root, '[data-glhk-verification]', verified ? 'HK DPO ALS 已核實' : '未核實本機文字');
      setText(root, '[data-glhk-record]', draftRecord || '—');
    }

    all(root, '[data-glhk-gproperty-entry]').forEach(function (link) {
      var intent = safeIntent(link.getAttribute('data-intent'), 'property-analysis');
      link.href = safeUrl(
        gate,
        root.getAttribute('data-gproperty-entry') ||
          link.getAttribute('href') ||
          '/pages/gproperty',
        intent,
        draftRecord
      );
    });

    all(root, '[data-glhk-intake-link]').forEach(function (link) {
      var base = window.location.pathname + '#contact-portal-form';
      link.href = safeUrl(gate, base, 'property-service', draftRecord);
    });

    gate
      .publicReadiness(root.getAttribute('data-readiness-endpoint'))
      .then(function (response) {
        var body = response.body || {};
        var panel = root.querySelector('[data-glhk-readiness]');

        if (response.response_ok && gate.isServingReady(body)) {
          if (panel) panel.setAttribute('data-state', 'ready');
          setText(root, '[data-glhk-readiness-title]', '官方家庭資料已可服務');
          setText(
            root,
            '[data-glhk-readiness-copy]',
            '已通過 public_redacted readiness contract；後續自動分析仍會再次核對地址與資料狀態。'
          );
          setText(root, '[data-glhk-readiness-badge]', '可分析');
          return;
        }

        if (response.response_ok && gate.isPublicRedacted(body)) {
          if (panel) panel.setAttribute('data-state', 'blocked');
          setText(root, '[data-glhk-readiness-title]', '官方家庭資料仍在同步');
          setText(
            root,
            '[data-glhk-readiness-copy]',
            '需要繼續人工核實或推進？官方家庭資料仍在同步中，目前沒有生成自動分數或物業推薦。GLHK 可協助你人工核實物業資料、比較實際選擇、安排睇樓或處理放盤需求。'
          );
          setText(root, '[data-glhk-readiness-badge]', '人工服務可用');
          return;
        }

        throw new Error('invalid_readiness_contract');
      })
      .catch(function () {
        var panel = root.querySelector('[data-glhk-readiness]');
        if (panel) panel.setAttribute('data-state', 'error');
        setText(root, '[data-glhk-readiness-title]', '暫時無法核對自動分析狀態');
        setText(
          root,
          '[data-glhk-readiness-copy]',
          '自動分析保持 Fail Closed；GLHK 人工核實與服務查詢仍可使用。'
        );
        setText(root, '[data-glhk-readiness-badge]', '人工服務可用');
      });
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 3. LIFECYCLE & EVENT BOOTSTRAP
   * ────────────────────────────────────────────────────────────────────────── */
  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT)) init(target);
    all(target, ROOT).forEach(init);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }
})(window, document);
